import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSequence } from '../context/SequenceContext';
import { useCRM } from '../context/CRMContext';
import SafeIcon from '../common/SafeIcon';
import { motion } from 'framer-motion';

const SequenceBuilder = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { sequences, updateSequenceSteps } = useSequence();
  const { emailTemplates } = useCRM();

  const [sequence, setSequence] = useState(null);
  const [steps, setSteps] = useState([]);
  const [activeStepId, setActiveStepId] = useState(null);

  useEffect(() => {
    if (id) {
      const found = sequences.find(s => s.sequence_id === id);
      if (found) {
        setSequence(found);
        setSteps(found.steps || []);
      }
    }
  }, [id, sequences]);

  const handleAddStep = (type) => {
    const newStep = {
      step_id: `new-${Date.now()}`,
      step_order: steps.length + 1,
      type,
      delay_days: type === 'Delay' ? 2 : 0,
      variant: 'Normal',
      template_id: ''
    };
    setSteps([...steps, newStep]);
    setActiveStepId(newStep.step_id);
  };

  const updateStep = (stepId, field, value) => {
    setSteps(prev => prev.map(s => s.step_id === stepId ? { ...s, [field]: value } : s));
  };

  const removeStep = (stepId) => {
    setSteps(prev => prev.filter(s => s.step_id !== stepId));
    if (activeStepId === stepId) setActiveStepId(null);
  };

  const handleSave = () => {
    updateSequenceSteps(id, steps);
    alert('Sekvens lagret!');
  };

  if (!sequence) return <div className="p-8">Laster sekvens...</div>;

  const activeStep = steps.find(s => s.step_id === activeStepId);

  return (
    <div className="flex h-[calc(100vh-80px)] bg-slate-50">
      {/* LEFT: Steps Timeline */}
      <div className="w-96 bg-white border-r border-slate-200 flex flex-col">
        <div className="p-6 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-2">
            <button onClick={() => navigate('/sequences')} className="text-slate-400 hover:text-slate-600"><SafeIcon icon="⬅️" /></button>
            <h2 className="font-black text-slate-900 text-lg">{sequence.name}</h2>
          </div>
          <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">{steps.length} steg definert</p>
        </div>
        
        <div className="flex-1 overflow-y-auto p-4 space-y-4 relative">
          <div className="absolute left-8 top-4 bottom-4 w-0.5 bg-slate-100 -z-10"></div>
          
          {steps.map((step, index) => (
            <div key={step.step_id} onClick={() => setActiveStepId(step.step_id)} className={`relative pl-12 cursor-pointer group`}>
              {/* Connector Dot */}
              <div className={`absolute left-6 top-4 w-5 h-5 rounded-full border-2 flex items-center justify-center z-10 transition-all ${activeStepId === step.step_id ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white border-slate-300 text-slate-400'}`}>
                <span className="text-[10px] font-black">{index + 1}</span>
              </div>
              
              <div className={`p-4 rounded-xl border-2 transition-all ${activeStepId === step.step_id ? 'bg-primary-50 border-primary-200 shadow-md' : 'bg-white border-slate-100 hover:border-slate-300'}`}>
                <div className="flex justify-between items-center mb-1">
                  <span className={`text-[10px] font-black uppercase tracking-widest ${activeStepId === step.step_id ? 'text-primary-700' : 'text-slate-500'}`}>
                    {step.type === 'Delay' ? 'Ventepause' : step.type === 'Email' ? 'Send E-post' : 'Oppgave'}
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); removeStep(step.step_id); }} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity">
                    <SafeIcon icon="✕" />
                  </button>
                </div>
                
                {step.type === 'Email' && (
                  <p className="text-sm font-bold text-slate-800">
                    {emailTemplates.find(t => t.id === step.template_id)?.name || 'Velg mal...'}
                  </p>
                )}
                {step.type === 'Delay' && (
                  <p className="text-sm font-bold text-slate-800">{step.delay_days} dager</p>
                )}
                {step.type === 'Task' && (
                  <p className="text-sm font-bold text-slate-800">{step.task_title || 'Beskriv oppgave...'}</p>
                )}
              </div>
            </div>
          ))}

          <div className="pl-12 pt-4">
            <div className="grid grid-cols-3 gap-2">
              <button onClick={() => handleAddStep('Email')} className="p-2 border border-dashed border-slate-300 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-50 hover:text-primary-600 hover:border-primary-300 flex flex-col items-center gap-1">
                <SafeIcon icon="📧" /> E-post
              </button>
              <button onClick={() => handleAddStep('Delay')} className="p-2 border border-dashed border-slate-300 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-50 hover:text-amber-600 hover:border-amber-300 flex flex-col items-center gap-1">
                <SafeIcon icon="⏳" /> Pause
              </button>
              <button onClick={() => handleAddStep('Task')} className="p-2 border border-dashed border-slate-300 rounded-lg text-xs font-bold text-slate-500 hover:bg-slate-50 hover:text-emerald-600 hover:border-emerald-300 flex flex-col items-center gap-1">
                <SafeIcon icon="✅" /> Oppgave
              </button>
            </div>
          </div>
        </div>

        <div className="p-4 border-t border-slate-200 bg-slate-50">
          <button onClick={handleSave} className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold text-sm hover:bg-slate-800 transition-all shadow-lg">
            Lagre Sekvens
          </button>
        </div>
      </div>

      {/* RIGHT: Config Area */}
      <div className="flex-1 bg-slate-50/50 p-12 flex items-center justify-center">
        {activeStep ? (
          <motion.div key={activeStep.step_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-2xl bg-white rounded-[2rem] shadow-xl border border-slate-200 p-8">
            <h3 className="text-xl font-black text-slate-900 mb-6 flex items-center gap-3">
              <span className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-xl">
                {activeStep.type === 'Email' ? '📧' : activeStep.type === 'Delay' ? '⏳' : '✅'}
              </span>
              Konfigurer Steg
            </h3>

            {activeStep.type === 'Email' && (
              <div className="space-y-6">
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Velg Mal</label>
                  <select 
                    value={activeStep.template_id} 
                    onChange={(e) => updateStep(activeStep.step_id, 'template_id', e.target.value)}
                    className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    <option value="">-- Velg e-postmal --</option>
                    {emailTemplates.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">AI Variant (Standard)</label>
                  <div className="flex gap-2">
                    {['Short', 'Normal', 'Direct'].map(v => (
                      <button 
                        key={v}
                        onClick={() => updateStep(activeStep.step_id, 'variant', v)}
                        className={`flex-1 py-2 rounded-lg text-xs font-bold border transition-all ${activeStep.variant === v ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-600 border-slate-200'}`}
                      >
                        {v}
                      </button>
                    ))}
                  </div>
                  <p className="text-[10px] text-slate-400 mt-2">Dette bestemmer tonen Copilot velger når utkastet genereres.</p>
                </div>
              </div>
            )}

            {activeStep.type === 'Delay' && (
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Ventetid (Dager)</label>
                <input 
                  type="number" 
                  value={activeStep.delay_days} 
                  onChange={(e) => updateStep(activeStep.step_id, 'delay_days', parseInt(e.target.value))}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-lg font-black outline-none focus:ring-2 focus:ring-primary-500"
                />
                <p className="text-sm text-slate-500 mt-2">Dager å vente før neste steg aktiveres.</p>
              </div>
            )}

            {activeStep.type === 'Task' && (
              <div>
                <label className="block text-xs font-black text-slate-500 uppercase tracking-widest mb-2">Oppgavebeskrivelse</label>
                <input 
                  type="text" 
                  value={activeStep.task_title} 
                  onChange={(e) => updateStep(activeStep.step_id, 'task_title', e.target.value)}
                  className="w-full p-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="F.eks. Ring kunden, Sjekk LinkedIn..."
                />
              </div>
            )}

          </motion.div>
        ) : (
          <div className="text-center text-slate-400">
            <SafeIcon icon="👆" className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p className="font-bold text-sm">Velg et steg til venstre for å redigere</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default SequenceBuilder;